import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { useToast } from '@shared/hooks/useToast';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BeepingTeamContacts } from '../../../List/models/Clients';
import { useUpdateTeamContact } from '../../hooks/useUpdateTeamContact';

interface EditContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: number;
  contact: BeepingTeamContacts | null;
  onSuccess?: () => void;
}

export const EditContactModal = ({ open, onOpenChange, teamId, contact, onSuccess }: EditContactModalProps) => {
  const { t } = useTranslation('viewClientModule');
  const { toast } = useToast();
  const { updateContactAsync, isPending, reset } = useUpdateTeamContact(teamId);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens with contact data
  useEffect(() => {
    if (open && contact) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        role: contact.role || '',
      });
      setErrors({});
    }
  }, [open, contact]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('tabs.contacts.modal.errors.nameRequired');
    }

    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t('tabs.contacts.modal.errors.emailInvalid');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        role: contact.role || '',
      });
    }
    setErrors({});
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!contact || !validateForm()) {
      return;
    }

    try {
      await updateContactAsync({
        contactId: contact.id!,
        data: {
          name: formData.name.trim(),
          email: formData.email.trim() || undefined,
          phone: formData.phone.trim() || undefined,
          role: formData.role.trim() || undefined,
        },
      });

      toast({
        title: t('tabs.contacts.modal.editSuccess.title'),
        description: t('tabs.contacts.modal.editSuccess.description'),
      });

      onSuccess?.();
      handleClose();
    } catch (error: any) {
      // Handle validation errors from server
      if (error?.errors) {
        const serverErrors: Record<string, string> = {};
        Object.entries(error.errors).forEach(([field, messages]) => {
          serverErrors[field] = Array.isArray(messages) ? messages[0] : String(messages);
        });
        setErrors(serverErrors);
      } else {
        toast({
          title: t('tabs.contacts.modal.editError.title'),
          description: t('tabs.contacts.modal.editError.description'),
          variant: 'destructive',
        });
      }
    }
  };

  if (!contact) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('tabs.contacts.modal.editTitle')}</DialogTitle>
          <DialogDescription>{t('tabs.contacts.modal.editDescription')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-contact-name">
              {t('tabs.contacts.name')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-contact-name"
              name="edit-contact-name"
              autoComplete="off"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('tabs.contacts.modal.namePlaceholder')}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="edit-contact-email">{t('tabs.contacts.email')}</Label>
            <Input
              id="edit-contact-email"
              name="edit-contact-email"
              type="email"
              autoComplete="off"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder={t('tabs.contacts.modal.emailPlaceholder')}
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="edit-contact-phone">{t('tabs.contacts.phone')}</Label>
            <Input
              id="edit-contact-phone"
              name="edit-contact-phone"
              type="tel"
              autoComplete="off"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder={t('tabs.contacts.modal.phonePlaceholder')}
              className={errors.phone ? 'border-destructive' : ''}
            />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="edit-contact-role">{t('tabs.contacts.role')}</Label>
            <Input
              id="edit-contact-role"
              name="edit-contact-role"
              autoComplete="off"
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              placeholder={t('tabs.contacts.modal.rolePlaceholder')}
              className={errors.role ? 'border-destructive' : ''}
            />
            {errors.role && <p className="text-sm text-destructive">{errors.role}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            {t('tabs.contacts.modal.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isPending || !formData.name.trim()}>
            {isPending ? t('tabs.contacts.modal.saving') : t('tabs.contacts.modal.update')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
